import { useEffect, useRef, useState } from 'react';
import './styles.css';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import LanguageBox from '../../components/LanguageBox/LanguageBox';
import ninaFeliz from '../../assets/images/nina-feliz.svg';
import { AppStoreButton } from 'react-mobile-app-button';
import useCommonFunctions from '../../hooks/useCommonFunctions';
import LogoutButton from '../../components/LogoutButton/LogoutButton';
import { QRCodeSVG } from 'qrcode.react';
import ConfettiExplosion from 'react-confetti-explosion';
import { ToastContainer } from 'react-toastify';
import PaymentController from '../../controllers/PaymentController.js/PaymentController';
import { trackPurchase } from '../../utils/gtm';

const logo = require('../../assets/images/fably-logo.png');
const url = process.env.REACT_APP_APP_URL;

function SuccessPage() {
  const { t } = useTranslation();
  const { goTo } = useCommonFunctions();
  const { verifyCheckoutSession } = PaymentController();
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  const [isExploding, setIsExploding] = useState(false);
  const [isValidPurchase, setIsValidPurchase] = useState(false);
  const [isCheckingSession, setIsCheckingSession] = useState(true);
  const hasTrackedPurchase = useRef(false);

  useEffect(() => {
    document.title = t('congratulations');
  }, [t]);

  useEffect(() => {
    let isMounted = true;

    const validateSession = async () => {
      if (!sessionId) {
        if (isMounted) {
          setIsCheckingSession(false);
          setIsValidPurchase(false);
        }
        return;
      }

      const response = await verifyCheckoutSession(sessionId);

      if (!isMounted) return;

      const paid = Boolean(response?.success && response?.paid);

      setIsValidPurchase(paid);
      setIsCheckingSession(false);

      if (paid && !hasTrackedPurchase.current) {
        hasTrackedPurchase.current = true;

        trackPurchase({
          transaction_id: response.sessionId,
          value:
            typeof response.amountTotal === 'number'
              ? response.amountTotal / 100
              : undefined,
          currency: response.currency?.toUpperCase() || 'USD',
          plan_id: response.subscription || undefined,
        });

        setIsExploding(true);

        setTimeout(() => {
          if (isMounted) setIsExploding(false);
        }, 2000);
      }
    };

    validateSession();

    return () => {
      isMounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  if (isCheckingSession) {
    return (
      <div className="general-container">
        <div className="lg-background" />
        <LanguageBox />
        <div className="container-l">
          <div className="sub-container-start">
            <img
              src={logo}
              alt={t('fablyLogo')}
              className="fablyLogo desktop-image mt-60"
            />
            <div className="mt-40 mb-30 flex-column align-center-h">
              <h3 className="cl-red">{t('loading')}</h3>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!isValidPurchase) {
    return (
      <div className="general-container">
        <div className="lg-background" />
        <LanguageBox />
        <LogoutButton />
        <div className="container-l">
          <ToastContainer hideProgressBar={true} className="toast" />
          <div className="sub-container-start">
            <img
              src={logo}
              alt={t('fablyLogo')}
              className="fablyLogo desktop-image mt-60"
            />
            <div className="mt-40 mb-30 flex-column align-center-h">
              <h3 className="cl-red">{t('paymentNotVerified')}</h3>
              <p className="nm-bottom-text sm-text">
                {t('paymentNotVerifiedDescription')}
              </p>
              <button
                type="button"
                onClick={() => goTo('/plans')}
                className="loginButton mb-100"
              >
                {t('backToPlans')}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="general-container">
      <div className="lg-background" />
      <LanguageBox />
      <LogoutButton />
      <img
        src={ninaFeliz}
        alt={t('ninaFeliz')}
        className="mobile-image big-img"
      />
      <div className="container-l">
        <ToastContainer
          hideProgressBar={true}
          className="toast"
          autoClose={3000}
          pauseOnFocusLoss={false}
        />
        {isExploding && (
          <ConfettiExplosion
            particleCount={300}
            zIndex={100}
            force={0.7}
            particleSize={8}
            width={window.innerWidth}
            height={window.innerHeight * 1.5}
          />
        )}
        <div className="sub-container-start">
          <img
            src={logo}
            alt={t('fablyLogo')}
            className="fablyLogo desktop-image mt-60"
          />
          <div className="mt-40 mb-30 flex-column align-center-h">
            <img
              src={ninaFeliz}
              alt={t('ninaFeliz')}
              className="desktop-image"
            />
            <h3 className="nm-top-text cl-red">
              {t('congratulationsPremiumSubscriber')}
            </h3>
            <p className="nm-bottom-text sm-text">
              {t('nowYouHaveFullAccess')}
            </p>
            <h3 className="cl-red">{t('downloadAndEnjoy')}</h3>
            <div className="qr-code">
              <QRCodeSVG
                value={url}
                size={160}
                level="H"
                bgColor="#FFFFFF"
                fgColor="#000000"
                imageSettings={{
                  src: '../../assets/images/fably-logo.png',
                  width: 20,
                  height: 20,
                }}
              />
            </div>
            <div className="store-btn">
              <AppStoreButton
                url={url}
                theme="dark"
                title={t('downloadOnThe')}
                width={160}
              />
            </div>
            <button
              type="button"
              onClick={() => (window.location.href = 'https://fably.app/')}
              className="loginButton mb-100"
            >
              {t('backToHomepage')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default SuccessPage;
